import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDate as formatLocaleDate } from '@/lib/format'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Pagination } from '@/components/Pagination'
import { EmptyState } from '@/components/EmptyState'
import { Mail, Search } from 'lucide-react'
import { useContactEmails } from '@/hooks/useApi'
import type { ContactSentEmail, EmailStatus } from '@/types/api'

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

const STATUS_PILLS: { value: EmailStatus | 'all'; labelKey: string }[] = [
  { value: 'all', labelKey: 'emails.status.all' },
  { value: 'delivered', labelKey: 'emails.status.delivered' },
  { value: 'opened', labelKey: 'emails.status.opened' },
  { value: 'clicked', labelKey: 'emails.status.clicked' },
  { value: 'bounced', labelKey: 'emails.status.bounced' },
  { value: 'failed', labelKey: 'emails.status.failed' },
]

const STATUS_BADGE_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info'> = {
  pending: 'secondary',
  sent: 'default',
  delivered: 'success',
  opened: 'info',
  clicked: 'info',
  bounced: 'destructive',
  failed: 'destructive',
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return ''
  return formatLocaleDate(dateStr, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function formatShortDate(dateStr: string | null): string {
  if (!dateStr) return ''
  return formatLocaleDate(dateStr, {
    day: 'numeric',
    month: 'short',
  })
}

function EmailTimeline({ email }: { email: ContactSentEmail }) {
  const { t } = useTranslation('contacts')
  const events: { key: string; label: string; date: string | null }[] = [
    { key: 'sent', label: t('emails.timeline.sent'), date: email.sent_at },
    { key: 'delivered', label: t('emails.timeline.delivered'), date: email.delivered_at },
    { key: 'opened', label: t('emails.timeline.opened'), date: email.opened_at },
    { key: 'clicked', label: t('emails.timeline.clicked'), date: email.clicked_at },
    { key: 'replied', label: t('emails.timeline.replied'), date: email.replied_at },
  ]

  if (email.status === 'bounced' || email.status === 'failed') {
    return (
      <div className="flex gap-3 text-xs text-muted-foreground">
        {email.sent_at && <span>{t('emails.timeline.sent')} {formatShortDate(email.sent_at)}</span>}
        <span className="text-destructive">
          {email.status === 'bounced' ? t('emails.timeline.bounced') : t('emails.timeline.failed')}{' '}
          {formatShortDate(email.sent_at)}
        </span>
      </div>
    )
  }

  return (
    <div className="flex gap-3 text-xs text-muted-foreground">
      {events
        .filter((e) => e.date)
        .map((e) => (
          <span key={e.key}>
            {e.label} {formatShortDate(e.date)}
          </span>
        ))}
      {/* Senza tracciamento un'apertura mancante non vuol dire "non letta" (#368) */}
      {email.open_tracked === false && <span>{t('emails.timeline.opensNotTracked')}</span>}
    </div>
  )
}

function BounceReason({ email }: { email: ContactSentEmail }) {
  const { t } = useTranslation('contacts')
  const reason = t(`emails.bounceReason.categories.${email.bounce_category}`, {
    defaultValue: t('emails.bounceReason.categories.altro'),
  })
  return (
    <p className="mt-1 text-xs text-destructive">
      {email.bounce_code
        ? t('emails.bounceReason.withCode', { reason, code: email.bounce_code })
        : t('emails.bounceReason.label', { reason })}
    </p>
  )
}

function EmailCard({ email }: { email: ContactSentEmail }) {
  const { t } = useTranslation('contacts')
  const isBounced = email.status === 'bounced' || email.status === 'failed'

  return (
    <div
      className={`rounded-lg border bg-card p-4 ${isBounced ? 'border-l-4 border-l-destructive' : ''}`}
    >
      <div className="flex justify-between items-start mb-1">
        <div className="min-w-0 flex-1">
          <p className="font-semibold truncate">{email.subject}</p>
          <p className="text-xs text-muted-foreground">
            {email.sequence_name} · {t('emails.card.step', { number: email.step_number })} ·{' '}
            {t('emails.card.via', { account: email.email_account_email })}
          </p>
        </div>
        <div className="flex items-center gap-2 ml-4 shrink-0">
          <Badge variant={STATUS_BADGE_VARIANT[email.status] ?? 'secondary'}>
            {email.status}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {formatDate(email.sent_at || email.created_at)}
          </span>
        </div>
      </div>

      <div className="mt-2">
        <EmailTimeline email={email} />
      </div>

      {email.status === 'bounced' && email.bounce_category && <BounceReason email={email} />}

      {email.replies.length > 0 && (
        <div className="mt-3 space-y-2">
          {email.replies.map((reply) => (
            <div
              key={reply.id}
              className={`border-l-2 pl-3 py-2 rounded-r-sm ${
                reply.is_autoreply
                  ? 'border-l-muted-foreground/30 bg-muted/30'
                  : 'border-l-primary/40 bg-primary/5'
              }`}
            >
              <p className="text-xs text-muted-foreground">
                {reply.is_autoreply && t('emails.card.auto')}
                {reply.from_email} · {formatDate(reply.received_at)}
              </p>
              {reply.snippet && (
                <p className="text-sm mt-0.5">{reply.snippet}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function ContactEmailList({ contactId }: { contactId: string }) {
  const { t } = useTranslation('contacts')
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const debouncedSearch = useDebounce(searchInput, 300)

  const { data, isLoading } = useContactEmails(contactId, page, 20, {
    status: statusFilter !== 'all' ? statusFilter : undefined,
    search: debouncedSearch || undefined,
  })

  const hasActiveFilters = statusFilter !== 'all' || !!debouncedSearch

  return (
    <div>
      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('emails.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value)
              setPage(1)
            }}
            className="pl-9"
          />
        </div>
        <div className="flex gap-1">
          {STATUS_PILLS.map((pill) => (
            <button
              key={pill.value}
              onClick={() => {
                setStatusFilter(pill.value)
                setPage(1)
              }}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                statusFilter === pill.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {t(pill.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
      ) : data && data.items.length > 0 ? (
        <>
          <div className="space-y-3">
            {data.items.map((email) => (
              <EmailCard key={email.id} email={email} />
            ))}
          </div>
          <div className="mt-4">
            <Pagination
              page={page}
              totalPages={data.pages}
              onPageChange={setPage}
            />
          </div>
        </>
      ) : (
        <EmptyState
          icon={Mail}
          title={
            hasActiveFilters
              ? t('emails.empty.noResultsTitle')
              : t('emails.empty.noEmailsTitle')
          }
          description={
            hasActiveFilters
              ? t('emails.empty.tryDifferentFilters')
              : t('emails.empty.willAppear')
          }
          action={
            hasActiveFilters
              ? {
                  label: t('emails.empty.resetFilters'),
                  onClick: () => {
                    setStatusFilter('all')
                    setSearchInput('')
                    setPage(1)
                  },
                }
              : undefined
          }
        />
      )}
    </div>
  )
}
